import { Avatar, Box, Container, Stack, Typography } from "@mui/material";
import React from "react";
import checkmark_icon from "../../Assets/checkmark_icon.png";
import DataCenters from "./Datacenters";
import security_and_compliance from "../../Assets/security_and_compliance.png";

// Shared styles for better consistency and maintainability
const styles = {
  container: {
    textAlign: "center",
  },
  heroTitle: {
    fontWeight: "bold",
    fontSize: { xs: "2.5rem", md: "4rem" },
    color: "#1662A7",
    fontFamily: "Roboto",
    textShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    lineHeight: 1.2,
  },
  introText: {
    fontSize: { xs: "1rem", md: "1.2rem" },
    color: "#6E6B7B",
    lineHeight: 1.7,
    fontFamily: "Roboto",
    maxWidth: "800px",
    mx: "auto",
  },
  sectionTitle: {
    fontWeight: "bold",
    fontSize: { xs: "1.5rem", md: "2rem" },
    color: "#25B2E8",
    mb: 4,
  },
  featureItem: {
    p: 4,
    borderRadius: "16px",
    backgroundColor: "#ffffff",
    border: "2px solid #f0f0f0",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0px 12px 35px rgba(37, 178, 232, 0.2)",
      transform: "translateY(-5px)",
      borderColor: "#25B2E8",
      backgroundColor: "#fafbff",
    },
    mb: 3,
    width: "80%",
    mx: "auto",
  },
  featureTitle: {
    fontWeight: "bold",
    fontSize: { xs: "1.2rem", md: "1.4rem" },
    color: "#1662A7",
    mb: 2,
    lineHeight: 1.3,
  },
  featureDescription: {
    fontSize: { xs: "1rem", md: "1.1rem" },
    color: "#6E6B7B",
    lineHeight: 1.7,
    textAlign: "left",
  },
  checkIcon: {
    width: 40,
    height: 40,
    filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.15))",
    backgroundColor: "#e8f4f8",
    borderRadius: "50%",
    p: 1,
  },
};

const SECURITY_MEASURES = [
  {
    title: "Robust data encryption",
    description:
      "We use industry-standard encryption protocols to protect your sensitive data.",
  },
  {
    title: "Secure data storage",
    description:
      "Our data centers adhere to strict security standards and are regularly audited.",
  },
  {
    title: "Regular security assessments",
    description:
      "We conduct ongoing security reviews to identify and address potential vulnerabilities.",
  },
  {
    title: "Compliance with industry standards",
    description:
      "We comply with relevant data privacy and security regulations, such as GDPR, CCPA, and HIPAA, among others.",
  },
];

const COMPLIANCE_EFFORTS = [
  {
    title: "Your digital signatures are legally binding",
    description:
      "Our solutions meet the requirements for legally enforceable digital signatures in various jurisdictions.",
  },
  {
    title: "Your data is handled responsibly",
    description: "We adhere to data privacy laws and regulations.",
  },
  {
    title: "Your business operations are protected",
    description:
      "We help you mitigate risks associated with document fraud and unauthorized access.",
  },
];

const Section = ({ title, items }) => (
  <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: "#F8F8F8" }}>
    <Container maxWidth="lg" sx={styles.container}>
      <Typography variant="h3" sx={styles.sectionTitle}>
        {title}
      </Typography>

      <Stack spacing={4} alignItems="center">
        {items.map((item, index) => (
          <Box key={index} sx={styles.featureItem}>
            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Avatar
                src={checkmark_icon}
                sx={styles.checkIcon}
                alt="Checkmark"
              />
              <Box sx={{ flex: 1 }}>
                <Typography sx={styles.featureTitle}>{item.title}</Typography>
                <Typography sx={styles.featureDescription}>
                  {item.description}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Container>
  </Box>
);

function SecurityCompliance() {
  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          mt: { xs: 8, md: 10 },
          pt: { xs: 6, md: 12 },
          pb: { xs: 4, md: 8 },
          backgroundImage: `linear-gradient(rgba(22, 98, 167, 0.1), rgba(37, 178, 232, 0.05)), url(${security_and_compliance})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "35vh", md: "10vh" },
        }}
      >
        <Container maxWidth="lg" sx={styles.container}>
          <Typography variant="h1" sx={styles.heroTitle}>
            Security and Compliance
          </Typography>
        </Container>
      </Box>

      {/* Introduction Section */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg" sx={styles.container}>
          <Typography sx={styles.introText}>
            RequireSign is committed to providing a secure and compliant digital
            signature solution. We leverage state-of-the-art technology,
            including blockchain, to ensure the integrity and authenticity of
            your documents.
          </Typography>
        </Container>
      </Box>

      {/* Security Measures Section */}
      <Box sx={{ mt: { xs: 4, md: 6 } }}>
        <Section
          title="Our security measures include:"
          items={SECURITY_MEASURES}
        />
      </Box>

      {/* Compliance Efforts Section */}
      <Box sx={{ backgroundColor: "#ffffff" }}>
        <Section
          title="Our compliance efforts ensure that:"
          items={COMPLIANCE_EFFORTS}
        />
      </Box>

      {/* Data Centers Section */}
      <DataCenters />
    </>
  );
}

export default SecurityCompliance;
